import styled from 'styled-components'

import { Flex, Heading, StatCard, Text } from '@percent/lemonade'
import { formatAmount } from '@percent/utility'

import { useOrganisationProfileContext } from '../../context/OrganisationProfileContext'
import { useTranslation } from '../../hooks/useTranslation'
import { OrganisationDonations } from '../../types/organisationProfile.types'
import { ProfileLink } from '../ProfileLink/ProfileLink'
import { SectionTitle } from '../SectionTitle/SectionTitle'

const Tiles = styled.div`
  ${({ theme }) => `
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(calc(${theme.spacing.xxl} * 4), 1fr));
    gap: ${theme.spacing.m};
  `}
`

// Decimal places from Intl, so hosts needn't pass currency info.
const toMajorUnits = (amount: number, currency: string) =>
  amount /
  10 **
    (new Intl.NumberFormat('en', {
      style: 'currency',
      currency,
    }).resolvedOptions().maximumFractionDigits ?? 2)

export type OrganisationDonationSummaryProps = {
  organisationId: string
  donations: OrganisationDonations
}

export function OrganisationDonationSummary({
  organisationId,
  donations,
}: Readonly<OrganisationDonationSummaryProps>) {
  const { t, locale } = useTranslation()
  const { links } = useOrganisationProfileContext()
  const hasDonations = donations.totals.length > 0

  const formatDate = (date: string | null) =>
    date
      ? new Date(date).toLocaleDateString(locale, {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        })
      : '—'

  return (
    <Flex
      direction="column"
      gap={16}
      data-testid="organisation-donation-summary"
    >
      <Flex justify="space-between" align="center" gap={16}>
        <Heading level="h2">
          <SectionTitle>{t('profile.donations.title')}</SectionTitle>
        </Heading>
        {hasDonations && links.donations && (
          <ProfileLink href={links.donations(organisationId)}>
            {t('profile.donations.viewAll')}
          </ProfileLink>
        )}
      </Flex>
      {!hasDonations ? (
        <Text>{t('profile.donations.empty')}</Text>
      ) : (
        <Tiles>
          {donations.totals.map(({ amount, currency, count }) => (
            <StatCard
              key={currency}
              label={t('profile.donations.total')}
              value={formatAmount({
                currencyCode: currency,
                value: toMajorUnits(amount, currency),
                language: locale,
              })}
              subtitle={t('profile.donations.subtitle', { currency, count })}
            />
          ))}
          <StatCard
            label={t('profile.donations.firstDonation')}
            value={formatDate(donations.firstDonationAt)}
          />
          <StatCard
            label={t('profile.donations.lastDonation')}
            value={formatDate(donations.lastDonationAt)}
          />
        </Tiles>
      )}
    </Flex>
  )
}
